import React from 'react';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';
import { Box, Typography, Button, Container, Grid } from '@mui/material';

// Error Fallback Component
const ErrorFallback = ({ error, resetErrorBoundary }) => {
    const handleReload = () => {
        window.location.reload();
    };

    const handleGoHome = () => {
        window.location.href = '/';
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Container maxWidth="md" sx={{ mt: 8, mb: 4 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                            p: 4,
                            border: '1px solid #e0e0e0',
                            borderRadius: 2,
                            backgroundColor: '#fafafa',
                            backgroundImage: 'url(/images/bg.jpg)',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                            position: 'relative',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                borderRadius: 2,
                                zIndex: 1
                            },
                            '& > *': {
                                position: 'relative',
                                zIndex: 2
                            }
                        }}
                    >
                        <Typography variant="h4" color="error" gutterBottom>
                            Oops! Something went wrong
                        </Typography>
                        
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                            We're sorry, but something unexpected happened. Please try refreshing the page or contact support if the problem persists.
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                            <Button 
                                variant="contained" 
                                color="primary" 
                                onClick={resetErrorBoundary}
                                sx={{ minWidth: 120 }}
                            >
                                Try Again
                            </Button>
                            
                            <Button 
                                variant="outlined" 
                                color="primary" 
                                onClick={handleReload}
                                sx={{ minWidth: 120 }}
                            >
                                Reload Page
                            </Button>
                            
                            <Button 
                                variant="outlined" 
                                color="primary" 
                                onClick={handleGoHome}
                                sx={{ minWidth: 120 }}
                            >
                                Go Dashboard
                            </Button>
                        </Box>

                        {process.env.NODE_ENV === 'development' && error && (
                            <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, width: '100%' }}>
                                <Typography variant="h6" color="error" gutterBottom>
                                    Development Error Details:
                                </Typography>
                                <Typography variant="body2" component="pre" sx={{ 
                                    textAlign: 'left', 
                                    overflow: 'auto', 
                                    maxHeight: 200,
                                    fontSize: '0.75rem'
                                }}>
                                    {error.toString()}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Container>
            </Grid>
        </Grid>
    );
};

// Error Logger Function
const logError = () => {
    // Optional: log to error reporting service (e.g. Sentry)
};

// Main ErrorBoundary Component
export const ErrorBoundary = ({ children }) => {
    return (
        <ReactErrorBoundary
            FallbackComponent={ErrorFallback}
            onError={logError}
            onReset={() => {}}
        >
            {children}
        </ReactErrorBoundary>
    );
};
